// src/components/Studio/studioStyles.ts
// One place for the Studios & Community visual language.
//
// The rest of the app repeats long Tailwind gradient strings in 20+ files. Everything
// under /studios, /productions, /community and /casting composes from these instead,
// so the studio surface can be restyled from a single file.

/** Deep, even, cinematic ground. Used for whole pages — never a light SaaS grey. */
export const SURFACE = 'bg-[#0B0B0D] text-neutral-200';

/** Slightly lifted panel for cards and blocks sitting on SURFACE. */
export const PANEL = 'bg-white/[0.03] border border-white/10 rounded-2xl';

export const PANEL_HOVER =
  'transition-colors duration-300 hover:border-orange-500/40 hover:bg-white/[0.055]';

/** The wordmark gradient, defined once. */
export const BRAND_GRADIENT_TEXT =
  'bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent';

export const PRIMARY_BUTTON =
  'inline-flex items-center justify-center gap-2 rounded-full bg-orange-600 px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-orange-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:cursor-not-allowed disabled:opacity-50';

export const SECONDARY_BUTTON =
  'inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-neutral-100 transition-colors duration-200 hover:border-white/40 hover:bg-white/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:cursor-not-allowed disabled:opacity-50';

export const GHOST_BUTTON =
  'inline-flex items-center gap-1.5 text-sm font-medium text-neutral-400 transition-colors duration-200 hover:text-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400';

/** Small uppercase label above a heading. */
export const EYEBROW =
  'text-xs font-semibold uppercase tracking-[0.2em] text-orange-400/90';

export const INPUT =
  'w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-neutral-100 placeholder:text-neutral-500 transition-colors duration-200 focus:border-orange-500/60 focus:bg-white/[0.06] focus:outline-none';

export const LABEL = 'mb-2 block text-sm font-medium text-neutral-300';

export const CONTAINER = 'mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8';

/** Stage → chip colour. Keeps pipeline state readable at a glance. */
export const STAGE_TONE: Record<string, string> = {
  DEVELOPMENT: 'bg-sky-500/10 text-sky-300 border-sky-500/25',
  PRE_PRODUCTION: 'bg-violet-500/10 text-violet-300 border-violet-500/25',
  IN_PRODUCTION: 'bg-orange-500/15 text-orange-300 border-orange-500/30',
  POST_PRODUCTION: 'bg-amber-500/10 text-amber-300 border-amber-500/25',
  RELEASED: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/25',
  ON_HOLD: 'bg-neutral-500/10 text-neutral-400 border-neutral-500/25',
  ARCHIVED: 'bg-neutral-500/10 text-neutral-500 border-neutral-500/20',
};

export const AVAILABILITY_TONE: Record<string, string> = {
  OPEN_TO_WORK: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/25',
  SELECTIVELY_AVAILABLE: 'bg-amber-500/10 text-amber-300 border-amber-500/25',
  ON_A_PRODUCTION: 'bg-orange-500/15 text-orange-300 border-orange-500/30',
  UNAVAILABLE: 'bg-neutral-500/10 text-neutral-400 border-neutral-500/25',
};

export const AVAILABILITY_LABEL: Record<string, string> = {
  OPEN_TO_WORK: 'Open to work',
  SELECTIVELY_AVAILABLE: 'Selectively available',
  ON_A_PRODUCTION: 'On a production',
  UNAVAILABLE: 'Not currently available',
};

/** "PRODUCTION_DESIGN" → "Production Design". Mirrors the backend's label logic. */
export function humanise(value: string | null | undefined): string {
  if (!value) return '';
  return value
    .split('_')
    .filter(Boolean)
    .map((part) => (part === 'VFX' ? 'VFX' : part.charAt(0) + part.slice(1).toLowerCase()))
    .join(' ');
}
